import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { Calendar, Users, Phone, MapPin, User, Search, FilterX, CheckCircle2, XCircle, Download, Pencil, Trash2, X, Check, LayoutList, Settings2, UserPlus, Send } from 'lucide-react';
import { PERMISSIONS } from '../lib/permissions';
import { usePermissions } from '../hooks/usePermissions';
import SessionsPanel from '../components/sen-plan/SessionsPanel';
import MessageBroadcastPanel from '../components/sen-plan/MessageBroadcastPanel';

interface Enrollment {
  id: number;
  parentName: string;
  phone: string;
  whatsappPhone: string;
  gender: string;
  age: string;
  district: string;
  attendees: number;
  sessionDate: string;
  sessionTime: string;
  verified: boolean;
  attended?: boolean;
  rsvpStatus?: 'attending' | 'not_attending' | string;
  source: string;
  createdAt: number;
  registrationTime?: string;
  whatsappStatus?: string | null;
  wamid?: string | null;
}

interface SessionOption {
  value: string;
  label: string;
}

const DEFAULT_SESSION_OPTIONS: SessionOption[] = [
  { value: '', label: '全部時段' },
];

const VERIFIED_OPTIONS = [
  { value: '', label: '全部狀態' },
  { value: '1', label: '已驗證' },
  { value: '0', label: '待驗證' },
];

const ATTENDED_OPTIONS = [
  { value: '', label: '全部報到' },
  { value: '1', label: '已報到' },
  { value: '0', label: '未報到' },
];

const AdminSenPlanEnrollments: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const { can } = usePermissions();
  const canWrite = can(PERMISSIONS.CUSTOMERS_WRITE);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessionDate, setSessionDate] = useState('');
  const [verified, setVerified] = useState('');
  const [attended, setAttended] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Enrollment | null>(null);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createForm, setCreateForm] = useState({
    parentName: '',
    phone: '',
    whatsappPhone: '',
    gender: '',
    age: '',
    district: '',
    attendees: 1,
    sessionDate: '',
    sessionTime: '11:00',
  });
  const [activeTab, setActiveTab] = useState<'enrollments' | 'sessions' | 'messages'>('enrollments');
  const [sessionOptions, setSessionOptions] = useState<SessionOption[]>(DEFAULT_SESSION_OPTIONS);

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchEnrollments();
  }, [token, navigate, sessionDate, verified, attended]);

  useEffect(() => {
    apiFetch('/api/public/admin/goodstore/sessions-summary')
      .then(r => r.json())
      .then((data: any) => {
        const sessions = Array.isArray(data.sessions) ? data.sessions : [];
        const opts: SessionOption[] = [{ value: '', label: '全部時段' }];
        for (const s of sessions) {
          if (s.status === 'active' || s.status === 'cancelled') {
            opts.push({ value: s.session_date, label: s.label });
          }
        }
        setSessionOptions(opts);
      })
      .catch(() => {});
  }, []);

  const fetchEnrollments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (sessionDate) params.set('sessionDate', sessionDate);
      if (verified) params.set('verified', verified);
      if (attended) params.set('attended', attended);
      if (search.trim()) params.set('search', search.trim());
      const res = await apiFetch(`/api/public/admin/goodstore/enrollments?${params.toString()}`);
      if (!res.ok) throw new Error('無法載入報名資料');
      const data = await res.json();
      setEnrollments(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchEnrollments();
  };

  const handleReset = () => {
    setSessionDate('');
    setVerified('');
    setAttended('');
    setSearch('');
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('確定要刪除這筆報名記錄嗎？此操作無法復原。')) return;
    try {
      const res = await apiFetch(`/api/public/admin/goodstore/enrollments/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('刪除失敗');
      setEnrollments(prev => prev.filter(e => e.id !== id));
    } catch (e: any) {
      setError(e.message || '刪除失敗');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    try {
      setSaving(true);
      const res = await apiFetch(`/api/public/admin/goodstore/enrollments/${editing.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          parentName: editing.parentName,
          phone: editing.phone,
          whatsappPhone: editing.whatsappPhone,
          gender: editing.gender,
          age: editing.age,
          district: editing.district,
          attendees: editing.attendees,
          sessionDate: editing.sessionDate,
          sessionTime: editing.sessionTime,
          verified: editing.verified,
        }),
      });
      if (!res.ok) throw new Error('儲存失敗');
      setEditing(null);
      fetchEnrollments();
    } catch (err: any) {
      setError(err.message || '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setCreateSaving(true);
      const res = await apiFetch('/api/public/admin/goodstore/enrollments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          parentName: createForm.parentName,
          phone: createForm.phone,
          whatsappPhone: createForm.whatsappPhone,
          gender: createForm.gender,
          age: createForm.age,
          district: createForm.district,
          attendees: createForm.attendees,
          sessionDate: createForm.sessionDate,
          sessionTime: createForm.sessionTime,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '新增失敗');
      setCreating(false);
      setCreateForm({
        parentName: '',
        phone: '',
        whatsappPhone: '',
        gender: '',
        age: '',
        district: '',
        attendees: 1,
        sessionDate: '',
        sessionTime: '11:00',
      });
      fetchEnrollments();
    } catch (err: any) {
      setError(err.message || '新增失敗');
    } finally {
      setCreateSaving(false);
    }
  };

  const handleCheckinToggle = async (enrollment: Enrollment) => {
    try {
      const res = await apiFetch(`/api/public/admin/goodstore/enrollments/${enrollment.id}/checkin`, {
        method: 'POST',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '報到狀態更新失敗');
      setEnrollments(prev => prev.map(e => e.id === enrollment.id ? { ...e, attended: data.attended } : e));
    } catch (err: any) {
      setError(err.message || '報到狀態更新失敗');
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('zh-HK', {
      month: 'long',
      day: 'numeric',
      weekday: 'long',
    });
  };

  const formatPhone = (phone: string) => {
    const s = String(phone || '');
    if (s.startsWith('852') && s.length === 11) {
      return `${s.slice(0, 3)} ${s.slice(3, 7)} ${s.slice(7)}`;
    }
    return s;
  };

  const formatRegistrationTime = (iso?: string) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('zh-HK', {
      timeZone: 'Asia/Hong_Kong',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const escapeCsv = (value: any) => {
    const s = String(value ?? '');
    if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };

  const csvPhone = (phone?: string) => {
    const s = String(phone || '').replace(/\D/g, '');
    return s.startsWith('852') ? s : s;
  };

  const rsvpLabel = (status?: string) => {
    if (status === 'attending') return '準時出席';
    if (status === 'not_attending') return '未能出席';
    return '未回覆';
  };

  const handleDownloadCsv = () => {
    const headers = ['家長姓名', '電話', 'WhatsApp電話', '性別', '年齡', '地區', '出席人數', '簡介會日期', '簡介會時間', '狀態', 'RSVP', '報到', '來源', '登記時間'];
    const rows = enrollments.map(e => [
      e.parentName,
      csvPhone(e.phone),
      csvPhone(e.whatsappPhone),
      e.gender,
      e.age,
      e.district,
      e.attendees,
      formatDate(e.sessionDate),
      e.sessionTime,
      e.verified ? '已驗證' : '待驗證',
      rsvpLabel(e.rsvpStatus),
      e.attended ? '已報到' : '未報到',
      e.source,
      formatRegistrationTime(e.registrationTime),
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(escapeCsv).join(','))].join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `goodstore-enrollments-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout currentPage="goodstore" onLogout={() => { localStorage.removeItem('admin_token'); localStorage.removeItem('admin_user'); navigate('/admin'); }}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Calendar className="w-6 h-6 text-orange-600" />
          「好餸·鄰里」簡介會報名
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">查看 SEN 計劃簡介會的報名記錄</p>
      </div>

      <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-800 dark:text-blue-200">
        <p className="font-bold mb-1">模板設置位置（如需修改文案）：</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Meta API 模板：後台「WhatsApp Center」→ 同步後可見 <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">sen_plan_t2_reminder_v3</code></li>
          <li>簡介會場次按「T-2」可發送提醒</li>
        </ul>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={() => setActiveTab('enrollments')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 transition ${
            activeTab === 'enrollments'
              ? 'border-orange-600 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
          }`}
        >
          <LayoutList size={16} />
          報名名單
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('sessions')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 transition ${
            activeTab === 'sessions'
              ? 'border-orange-600 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
          }`}
        >
          <Settings2 size={16} />
          簡介會場次
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('messages')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 transition ${
            activeTab === 'messages'
              ? 'border-orange-600 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
          }`}
        >
          <Send size={16} />
          訊息發放
        </button>
      </div>

      {activeTab === 'sessions' ? (
        <SessionsPanel />
      ) : activeTab === 'messages' ? (
        <MessageBroadcastPanel />
      ) : (
        <>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3 items-end">
          <div className="w-full md:w-1/4">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">簡介會時段</label>
            <select
              value={sessionDate}
              onChange={e => setSessionDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
            >
              {sessionOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="w-full md:w-1/5">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">驗證狀態</label>
            <select
              value={verified}
              onChange={e => setVerified(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
            >
              {VERIFIED_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="w-full md:w-1/5">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">報到狀態</label>
            <select
              value={attended}
              onChange={e => setAttended(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
            >
              {ATTENDED_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="w-full md:flex-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">搜尋姓名 / 電話</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="輸入姓名或電話"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
              />
            </div>
          </div>
          <div className="flex gap-2 w-full md:w-auto">
            <button
              type="submit"
              className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-bold hover:bg-orange-700 transition"
            >
              篩選
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-bold hover:bg-gray-50 dark:hover:bg-gray-600 transition flex items-center gap-1"
            >
              <FilterX size={14} />
              重設
            </button>
            <button
              type="button"
              onClick={handleDownloadCsv}
              disabled={enrollments.length === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-bold hover:bg-green-700 transition flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={14} />
              導出 CSV
            </button>
            {canWrite && (
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 transition flex items-center gap-1"
              >
                <UserPlus size={14} />
                新增報名
              </button>
            )}
          </div>
        </form>
      </div>

      {loading ? (
        <div className="text-center py-20 text-gray-500">載入中...</div>
      ) : enrollments.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-500">
          暫無報名記錄
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">家長姓名</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">電話</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">子女</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">地區</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">出席人數</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">簡介會日期</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">登記時間</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">狀態</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">RSVP</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">報到</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">訊息狀態</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">來源</th>
                  {canWrite && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">操作</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {enrollments.map(e => (
                  <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-gray-400" />
                        {e.parentName}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1">
                          <Phone size={14} className="text-gray-400" />
                          <span className="font-mono">{formatPhone(e.whatsappPhone || e.phone)}</span>
                        </div>
                        {e.whatsappPhone && e.phone && e.phone !== 'pending' && e.phone !== e.whatsappPhone && (
                          <span className="text-xs text-gray-400">填表：{formatPhone(e.phone)}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {e.gender} · {e.age}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      <div className="flex items-center gap-1">
                        <MapPin size={14} className="text-gray-400" />
                        {e.district}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      <div className="flex items-center gap-1">
                        <Users size={14} className="text-gray-400" />
                        {e.attendees} 位
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      <div>{formatDate(e.sessionDate)}</div>
                      <div className="text-xs text-gray-400">{e.sessionTime}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {formatRegistrationTime(e.registrationTime)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {e.verified ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold">
                          <CheckCircle2 size={12} />
                          已驗證
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
                          <XCircle size={12} />
                          待驗證
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {e.rsvpStatus === 'attending' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold">
                          <CheckCircle2 size={12} />
                          準時出席
                        </span>
                      ) : e.rsvpStatus === 'not_attending' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-bold">
                          <XCircle size={12} />
                          未能出席
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-bold">
                          未回覆
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {canWrite ? (
                        <button
                          type="button"
                          onClick={() => handleCheckinToggle(e)}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold transition ${
                            e.attended
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          title={e.attended ? '已報到，按一下取消' : '按一下標記報到'}
                        >
                          {e.attended ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                          {e.attended ? '已報到' : '未報到'}
                        </button>
                      ) : (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${
                          e.attended ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {e.attended ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                          {e.attended ? '已報到' : '未報到'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {e.whatsappStatus === 'read' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold" title="已讀取">
                          🔵 已讀
                        </span>
                      ) : e.whatsappStatus === 'delivered' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold" title="已送達">
                          🟢 已送達
                        </span>
                      ) : e.whatsappStatus === 'sent' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs font-bold" title="已發送">
                          🟡 已發送
                        </span>
                      ) : e.whatsappStatus === 'failed' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-bold" title="發送失敗">
                          🔴 失敗
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-gray-400 text-xs">
                          —
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {e.source || '-'}
                    </td>
                    {canWrite && (
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setEditing(e)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-bold text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition"
                          >
                            <Pencil size={12} />
                            編輯
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(e.id)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-bold text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition"
                          >
                            <Trash2 size={12} />
                            刪除
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">編輯報名資料</h3>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">家長姓名</label>
                  <input
                    type="text"
                    value={editing.parentName}
                    onChange={e => setEditing(prev => prev ? { ...prev, parentName: e.target.value } : null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">電話</label>
                  <input
                    type="text"
                    value={editing.phone}
                    onChange={e => setEditing(prev => prev ? { ...prev, phone: e.target.value } : null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">WhatsApp 電話</label>
                  <input
                    type="text"
                    value={editing.whatsappPhone}
                    onChange={e => setEditing(prev => prev ? { ...prev, whatsappPhone: e.target.value } : null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">子女性別</label>
                  <input
                    type="text"
                    value={editing.gender}
                    onChange={e => setEditing(prev => prev ? { ...prev, gender: e.target.value } : null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">子女年齡</label>
                  <input
                    type="text"
                    value={editing.age}
                    onChange={e => setEditing(prev => prev ? { ...prev, age: e.target.value } : null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">地區</label>
                  <input
                    type="text"
                    value={editing.district}
                    onChange={e => setEditing(prev => prev ? { ...prev, district: e.target.value } : null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">出席人數</label>
                  <input
                    type="number"
                    min={1}
                    value={editing.attendees}
                    onChange={e => setEditing(prev => prev ? { ...prev, attendees: Number(e.target.value) } : null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">簡介會日期</label>
                  <input
                    type="date"
                    value={editing.sessionDate}
                    onChange={e => setEditing(prev => prev ? { ...prev, sessionDate: e.target.value } : null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">簡介會時間</label>
                  <input
                    type="time"
                    value={editing.sessionTime}
                    onChange={e => setEditing(prev => prev ? { ...prev, sessionTime: e.target.value } : null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                    required
                  />
                </div>
                <div className="flex items-center gap-2 sm:col-span-2">
                  <input
                    id="verified"
                    type="checkbox"
                    checked={editing.verified}
                    onChange={e => setEditing(prev => prev ? { ...prev, verified: e.target.checked } : null)}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <label htmlFor="verified" className="text-sm font-medium text-gray-700 dark:text-gray-300">已驗證</label>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setEditing(null)}
                  className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-bold hover:bg-gray-50 dark:hover:bg-gray-600 transition"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-bold hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  {saving ? '儲存中...' : <><Check size={14} /> 儲存</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">新增報名（已驗證）</h3>
              <button
                type="button"
                onClick={() => setCreating(false)}
                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateSave} className="p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">家長姓名 *</label>
                  <input
                    type="text"
                    value={createForm.parentName}
                    onChange={e => setCreateForm(prev => ({ ...prev, parentName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">電話 *</label>
                  <input
                    type="text"
                    value={createForm.phone}
                    onChange={e => setCreateForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">WhatsApp 電話</label>
                  <input
                    type="text"
                    value={createForm.whatsappPhone}
                    onChange={e => setCreateForm(prev => ({ ...prev, whatsappPhone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">子女性別</label>
                  <input
                    type="text"
                    value={createForm.gender}
                    onChange={e => setCreateForm(prev => ({ ...prev, gender: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">子女年齡</label>
                  <input
                    type="text"
                    value={createForm.age}
                    onChange={e => setCreateForm(prev => ({ ...prev, age: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">地區</label>
                  <input
                    type="text"
                    value={createForm.district}
                    onChange={e => setCreateForm(prev => ({ ...prev, district: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">出席人數 *</label>
                  <input
                    type="number"
                    min={1}
                    value={createForm.attendees}
                    onChange={e => setCreateForm(prev => ({ ...prev, attendees: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">簡介會日期 *</label>
                  <input
                    type="date"
                    value={createForm.sessionDate}
                    onChange={e => setCreateForm(prev => ({ ...prev, sessionDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">簡介會時間 *</label>
                  <input
                    type="time"
                    value={createForm.sessionTime}
                    onChange={e => setCreateForm(prev => ({ ...prev, sessionTime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setCreating(false)}
                  className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-bold hover:bg-gray-50 dark:hover:bg-gray-600 transition"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={createSaving}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-bold hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  {createSaving ? '儲存中...' : <><Check size={14} /> 新增</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
        </>
      )}
    </AdminLayout>
  );
};

export default AdminSenPlanEnrollments;
